import { useEffect, useRef, useState } from "react";
import type { Me } from "../api/types";
import { afterBack, openLayer } from "../backstack";
import { reloadNow } from "../fresh";
import { NEWS, NEWS_VER, useNewsFresh } from "../news";
import { Face, nameOf, roleName } from "./AccountMenu";
import AiOrb from "./AiOrb";
import { ShellIcon, type ShellIconName } from "./ShellIcons";
import ThemeSwitch from "./ThemeSwitch";

export interface NavItem {
  key: string;
  label: string;
  icon: ShellIconName | "ai";
  /** Something waiting there: 对话's customers still to answer. */
  badge?: number;
  /** A customer is waiting: the number is red. */
  hot?: boolean;
  /** 小爱's line: on duty and how fast, or what is wrong. */
  sub?: string;
  /** Something wrong there: 小爱's connection, slow (warn) or down (bad). */
  tone?: "" | "warn" | "bad";
}

/** The bar's own icons (prototype tabbar): plain outlines — a house, a bubble, two people, bars — where the sidebar and
 *  更多 draw each page's two-tone icon. */
const BAR_ICON: Partial<Record<string, ShellIconName>> = { today: "home", leads: "chat", customers: "people", reports: "chart", followup: "send" };

function Ic({ it, on, bar }: { it: NavItem; on: boolean; bar?: boolean }) {
  if (it.icon === "ai") return <AiOrb size={on ? 24 : 22} tone={it.tone} />;
  return <ShellIcon name={(bar && BAR_ICON[it.key]) || it.icon} />;
}

/**
 * The phone's bar along the bottom (prototype v2.20 tabbar): four pages under
 * the thumb, each an icon in a pill over its name, the one on screen in jade.
 * On a phone the pages used to be a strip of tabs across the top that slid
 * sideways, and whichever was slid out of view — 对话 itself, often — had no tab
 * to press (owner, 2026-10-04: 「东西全挤一块非常难操作」).
 *
 * The rest are under 更多, a sheet from the bottom (prototype MoreSheet): a newer
 * build first when there is one, who is signed in, the other pages, 更新了什么,
 * the look, signing out.
 */
export default function PhoneNav({
  me,
  view,
  items,
  more,
  pending,
  fresh,
  onPick,
  onNews,
  onSignOut,
}: {
  me: Me;
  view: string;
  items: NavItem[];
  more: NavItem[];
  /** The page on screen while it is being read: its item turns (or 更多's, when it is in there). */
  pending: string;
  /** The console was updated under the open page. */
  fresh: boolean;
  onPick: (key: string) => void;
  onNews: () => void;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const newsFresh = useNewsFresh();
  // A page under 更多 that has something wrong marks 更多 itself, as a newer build does.
  const moreTone = fresh || more.some((m) => m.tone === "bad") ? "bad" : more.some((m) => m.tone) ? "warn" : "";
  // The sheet is a step of history: the phone's back closes it.
  const close = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!open) return;
    const c = openLayer(() => {
      close.current = null;
      setOpen(false);
    });
    close.current = () => {
      close.current = null;
      c();
      setOpen(false);
    };
    return () => close.current?.();
  }, [open]);
  // App writes the new #page once the sheet's step is off the history (afterBack).
  const pick = (k: string) => {
    close.current?.();
    onPick(k);
  };
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === "Escape" && close.current?.();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [open]);

  const inMore = more.some((m) => m.key === view);
  return (
    <>
      <nav className="pnav" aria-label="页面" style={{ ["--pnav-n" as string]: items.length + 1 }}>
        {items.map((it) => {
          const on = view === it.key;
          return (
            <button
              key={it.key}
              type="button"
              className={`pnav-i${on ? " on" : ""}`}
              aria-current={on ? "page" : undefined}
              aria-busy={pending === it.key || undefined}
              onClick={() => onPick(it.key)}
            >
              <span className="pnav-ic">
                <Ic it={it} on={on} bar />
                {!!it.badge && <b className={`pnav-bd${it.hot ? "" : " n"}`}>{it.badge > 99 ? "99+" : it.badge}</b>}
                {!it.badge && it.tone && <i className={`pnav-dot ${it.tone}`} aria-hidden="true" />}
              </span>
              <span className="pnav-t">{it.label}</span>
            </button>
          );
        })}
        <button
          type="button"
          className={`pnav-i${inMore || open ? " on" : ""}`}
          aria-expanded={open}
          aria-busy={(inMore && pending === view) || undefined}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="pnav-ic">
            <ShellIcon name="grid" />
            {moreTone && <i className={`pnav-dot ${moreTone}`} aria-label={fresh ? "CRM 有新版本" : undefined} />}
          </span>
          <span className="pnav-t">更多</span>
        </button>
      </nav>
      {open && (
        <div className="psheet-bg" onClick={() => close.current?.()}>
          <div className="psheet" role="dialog" aria-label="更多" onClick={(e) => e.stopPropagation()}>
            <i className="psheet-grab" aria-hidden="true" />
            {fresh && (
              <button type="button" className="psheet-fresh" onClick={reloadNow} aria-label="CRM 有新版本，按一下刷新">
                <span className="psheet-fresh-ic">
                  <ShellIcon name="refresh" />
                </span>
                <span className="psheet-fresh-t">
                  <b>CRM 有新版本</b>
                  <small>按一下就更新</small>
                </span>
                <span className="psheet-fresh-go">刷新</span>
              </button>
            )}
            <div className="psheet-who">
              <Face me={me} size={40} />
              <div>
                <b>{nameOf(me)}</b>
                <span>
                  {roleName(me)} · {me.email}
                </span>
              </div>
            </div>
            <div className="psheet-list">
              {more.map((m) => {
                const on = view === m.key;
                return (
                  <button key={m.key} type="button" className={`psheet-row${on ? " on" : ""}`} onClick={() => pick(m.key)}>
                    {/* 小爱 as ✦ in the row's box, like every other page's icon (prototype MoreSheet); the orb is the bar's and the sidebar's. */}
                    <span className="psheet-ic">{m.icon === "ai" ? <ShellIcon name="sparkles" /> : <Ic it={m} on={false} />}</span>
                    <span className="psheet-t">
                      <b>{m.label}</b>
                      {m.sub && <small className={m.tone || undefined}>{m.sub}</small>}
                    </span>
                    {!!m.badge && <span className={`psheet-bd${m.hot ? " hot" : ""}`}>{m.badge > 99 ? "99+" : m.badge}</span>}
                    <ShellIcon name="right" className="psheet-go" />
                  </button>
                );
              })}
              <button
                type="button"
                className="psheet-row"
                onClick={() => {
                  close.current?.();
                  // Opened once the sheet's own step is off the history, or the two steps cross.
                  afterBack(onNews);
                }}
              >
                <span className="psheet-ic brand">
                  <ShellIcon name="sparkles" />
                </span>
                <span className="psheet-t">
                  <b>更新了什么</b>
                  <small>
                    v{NEWS_VER} · {NEWS[0].date}
                  </small>
                </span>
                {newsFresh && <span className="psheet-new">新</span>}
                <ShellIcon name="right" className="psheet-go" />
              </button>
            </div>
            <div className="psheet-look">
              <span>外观</span>
              <ThemeSwitch />
            </div>
            <button
              type="button"
              className="psheet-out"
              onClick={() => {
                close.current?.();
                onSignOut();
              }}
            >
              <ShellIcon name="logout" />
              退出登录
            </button>
          </div>
        </div>
      )}
    </>
  );
}
